// Detail content for each service: pricing (placeholder), preparation steps, FAQs
export const SERVICE_DETAILS = {
  'mobile-phlebotomy': {
    title: 'Mobile Phlebotomy',
    heroImage: '/generated/hero_phlebotomy.png',
    tagline: 'The lab, delivered to your door.',
    intro:
      'Skilled phlebotomists come to your home, office, or preferred location for routine and specialty blood collection. Every visit is punctual, private, and handled with the discretion of a personal concierge.',
    highlights: [
      'Fully licensed and insured phlebotomists',
      'HIPAA-compliant handling from collection to lab',
      'Compatible with most major reference labs',
      'Available Sun–Sat, 7:00 AM – 8:00 PM',
    ],
    pricing: [
      { name: 'Single Home Draw', price: '$95', note: 'Per visit, one patient' },
      { name: 'Family / Multi-Patient Draw', price: '$75', note: 'Per additional patient, same visit' },
      { name: 'After-Hours Appointment', price: '+ $40', note: 'Additional fee added to any visit outside standard business hours' },
      { name: 'Specialty Kit', price: '$125', note: 'Per specialty kit required for the appointment' },
      { name: 'Specialty Kit — Additional', price: '$80', note: 'Each additional specialty kit at the same visit' },
      { name: 'Corporate / Office Draw', price: 'Custom quote', note: 'Volume pricing for teams' },
    ],
    prep: [
      'Confirm whether fasting is required with your ordering provider (typically 8–12 hours).',
      'Drink plenty of water the day before and morning of your appointment.',
      'Have a valid photo ID and any lab orders ready to hand off.',
      'Choose a private, comfortable seat with an armrest for the draw.',
      'Wear a top with sleeves that roll up easily above the elbow.',
    ],
    servicePaths: {
      eyebrow: 'HOW BOOKING WORKS',
      heading: 'Insured patients. Doctor-ordered testing.',
      intro:
        'Aurelia services insured patients with a valid doctor’s order. Bring your signed lab requisition to your appointment and we’ll handle the rest.',
      options: [
        {
          tag: 'PROVIDER-ORDERED',
          title: 'Bring your physician’s requisition',
          body:
            'Have a signed lab requisition from your physician or ordering healthcare provider? Hand it to our phlebotomist at your appointment — we collect the specimens and route them to the laboratory indicated on the order.',
          bullets: [
            'A valid doctor’s order is required for every appointment',
            'Bring the signed requisition and a valid photo ID',
            'We route the specimens to the lab specified on your order',
            'Results return directly to your ordering provider',
            'Ideal when insurance billing is handled by your provider or lab',
          ],
        },
      ],
      footnote:
        'Aurelia only accepts insured patients with a valid doctor’s order. We do not offer self-pay or client-selected testing. Aurelia does not diagnose medical conditions or interpret laboratory results — please review all results with a qualified healthcare provider.',
    },
    pricingNote:
      'The prices below are only for the concierge collection fee for each service. Prices are not including laboratory or testing fees.',
    testCategories: [
      'Annual Wellness & Basics',
      'Allergies & Environmental Exposures',
      'Anemia, Blood, & Bone Health',
      'Anxiety, Depression, & Brain Health',
      'Autoimmune Issues',
      'Cancer & Other Screenings',
      'Diabetes & Pancreatic Health',
      'Drug & Alcohol Screening',
      'Fitness & Performance',
      'Heart Health & Cholesterol',
      'Hormone & Thyroid Health',
      'Tests with Treatment Options',
      'Liver & Kidney Health',
      'Pregnancy & Fertility',
      'STDs & Sexual Health',
      'TB & Respiratory Infections',
      'Vaccination & Immunity',
      'Vitamins, Nutrition, & Digestion',
      'At-Home Tests and Home Collection Kits',
      'Health Profiles',
      'Men’s Health',
      'New Tests',
      'Pediatric Lab Tests',
      'Geriatric Lab Tests',
      'Popular Tests',
      'Women’s Health',
    ],
    prep: [
      'Confirm whether fasting is required with your ordering provider (typically 8–12 hours).',
      'Drink plenty of water the day before and morning of your appointment.',
      'Have a valid photo ID and any lab orders ready to hand off.',
      'Choose a private, comfortable seat with an armrest for the draw.',
      'Wear a top with sleeves that roll up easily above the elbow.',
    ],
    experience: {
      title: 'The Aurelia Experience',
      steps: [
        {
          title: 'Select your experience',
          body: 'Choose from our bespoke home draw options and specialty collection services — thoughtfully designed to complement your provider’s orders and individual health goals.',
        },
        {
          title: 'Secure your appointment',
          body: 'Payment is collected securely through our Square point-of-sale system before services are rendered. For your convenience, Aurelia also accepts Afterpay — our buy-now-pay-later option.',
        },
        {
          title: 'Schedule with ease',
          body: 'Choose a convenient appointment time and location — we come to you. Please also complete our Client Consent Form (available under Patient Forms) ahead of your appointment so we can keep your visit swift and seamless.',
        },
        {
          title: 'Relax. We’ll handle the collection.',
          body: 'Your trained Aurelia phlebotomy professional arrives prepared to provide a discreet, comfortable, and efficient specimen collection experience.',
        },
        {
          title: 'Your specimens, handled with care',
          body: 'Specimens are carefully labeled, prepared, and transported to the appropriate partner laboratory for testing.',
        },
        {
          title: 'Your results, your next step',
          body: 'Results are made available through the appropriate laboratory or ordering healthcare provider. Aurelia does not diagnose or interpret medical conditions.',
        },
        {
          title: 'Invest in your well-being',
          body: 'Take your results to your healthcare provider and use them to have a more informed conversation about your health, wellness, and next steps.',
        },
      ],
      closer: {
        primary: 'Your health. Your time. Your space.',
        secondary: 'That’s the Aurelia difference.',
      },
    },
    faqs: [
      { q: 'Do I need a doctor’s order?', a: 'Yes — Aurelia requires a valid doctor’s order for every mobile phlebotomy appointment. We service insured patients only, so please bring your signed lab requisition and a valid photo ID. We do not offer self-pay or client-selected testing.' },
      { q: 'Which labs do you work with?', a: 'We commonly work with Quest, LabCorp, and specialty labs. Let us know which your provider uses and we will coordinate the drop-off.' },
      { q: 'How quickly will I get results?', a: 'Most routine panels return in 24–48 hours. Specialty testing can take longer; we will keep you informed.' },
    ],
  },
  'bls-cpr-classes': {
    title: 'BLS CPR Classes',
    heroImage: '/generated/cpr_training.png',
    tagline: 'Life-saving skills. Concierge-level training.',
    intro:
      'American Heart Association Basic Life Support (BLS) certification courses for healthcare providers, professional teams, and prepared individuals — taught by experienced instructors in your space or ours.',
    highlights: [
      'American Heart Association–aligned curriculum',
      'Adult, child, and infant CPR + AED',
      'Choking relief and team-based resuscitation',
      'Digital certification card issued upon completion',
    ],
    pricing: [
      { name: 'Individual BLS Certification', price: '$65', note: 'Per person, scheduled classes' },
      { name: 'BLS Renewal', price: '$55', note: 'Current card required' },
      { name: 'On-Site Group Training (5–10)', price: 'Starting at $650', note: 'We come to your facility' },
      { name: 'Corporate Package (10+)', price: 'Custom quote', note: 'Discounted per-seat pricing' },
    ],
    prep: [
      'Wear comfortable clothing you can kneel and move in.',
      'Arrive 10 minutes early to sign in and receive materials.',
      'Bring photo ID; renewals should also bring current BLS card.',
      'Review the AHA BLS student manual if provided beforehand.',
    ],
    faqs: [
      { q: 'How long does the class take?', a: 'Initial certifications typically run 3–4 hours. Renewals are 2–3 hours.' },
      { q: 'How long is my certification valid?', a: 'AHA BLS cards are valid for 2 years. We will remind you before yours expires.' },
      { q: 'Can you train my whole team on-site?', a: 'Yes — we bring manikins, AED trainers, and all supplies to your office or facility.' },
    ],
  },
  'dna-testing': {
    title: 'DNA Testing',
    heroImage: '/generated/dna_testing.png',
    tagline: 'Answers you can trust, handled with discretion.',
    intro:
      'Confidential, court-admissible or personal DNA testing performed by certified collectors. Whether you need legally defensible results or private peace of mind, we handle every step with dignity.',
    highlights: [
      'Legal (court-admissible) chain-of-custody testing',
      'Non-legal / personal informational testing',
      'Paternity, maternity, sibling, and ancestry panels',
      'Rapid, laboratory-verified results',
    ],
    pricing: [
      { name: 'Non-Legal Paternity Test', price: 'Starting at $199', note: 'Alleged father + child' },
      { name: 'Legal Paternity Test', price: 'Starting at $395', note: 'Chain-of-custody, court-admissible' },
      { name: 'Additional Party', price: 'Starting at $95', note: 'Per additional participant' },
      { name: 'Sibling / Maternity Panel', price: 'Starting at $349', note: 'Non-legal, informational' },
    ],
    prep: [
      'Do not eat, drink, or smoke for 30 minutes before your collection.',
      'Legal testing requires a valid government-issued photo ID for every adult participant.',
      'For minors, bring a birth certificate or court order establishing guardianship.',
      'All participants must be present at the same collection appointment.',
    ],
    faqs: [
      { q: 'What is the difference between legal and non-legal testing?', a: 'Legal testing follows strict chain-of-custody rules and can be used in court. Non-legal testing gives you the same laboratory answer but without documentation for legal proceedings.' },
      { q: 'How long do results take?', a: 'Most results are available within 2–5 business days after the lab receives the samples.' },
      { q: 'Is the collection painful?', a: 'Not at all. DNA is collected via a gentle cheek swab — no needles involved.' },
    ],
  },
  'drug-screening': {
    title: 'Drug & Urine Screenings',
    heroImage: '/generated/drug_screening.png',
    tagline: 'Compliant, discreet, delivered on your schedule.',
    intro:
      'DOT and Non-DOT drug and alcohol screenings for employers, individuals, and legal cases. Every collection follows full protocol with dignity, privacy, and complete documentation.',
    highlights: [
      'DOT compliance for CDL and safety-sensitive roles',
      'Non-DOT employer panels (5, 10, 12 panel)',
      'Rapid point-of-care (POCT) screens for private employers',
      'Pre-employment, random, post-accident testing',
      'Court-ordered, probation, and personal testing',
    ],
    process: {
      title: 'How Our On-Site DOT Collection Works',
      intro:
        'For DOT (and Non-DOT) drug and urine screenings, we come directly to the employer\u2019s workplace. Our certified collector brings all supplies and performs the collection on-site so your team stays productive.',
      bullets: [
        'We travel to the employer\u2019s office with a complete DOT collection kit and all necessary supplies.',
        'The collection is performed in an appropriate, private restroom or a designated room at the employer\u2019s facility.',
        'The employer\u2019s actual physical location becomes the collection-site address on the DOT paperwork.',
        'If the employer does not have a suitable private space, we arrange a professional, secure meeting place for the client and the collector to complete the test.',
        'When testing at an alternate location, the client must bring the employer\u2019s CCF (Custody and Control Form) paperwork with them to the appointment.',
      ],
    },
    pricing: [
      { name: 'DOT 5-Panel Urine', price: 'Starting at $75', note: 'Federally regulated' },
      { name: 'Non-DOT 5-Panel Urine', price: 'Starting at $65', note: 'Employer or personal' },
      { name: 'Rapid Point-of-Care Screen', price: 'Starting at $55', note: 'Results in ~5 min · non-negatives confirmed by lab' },
      { name: 'Non-DOT 10-Panel Urine', price: 'Starting at $95', note: 'Expanded substances' },
      { name: 'On-Site Corporate Collection', price: 'Custom quote', note: 'For teams and events' },
    ],
    prep: [
      'Bring a valid government-issued photo ID.',
      'Drink water normally — do not over-hydrate before collection.',
      'For DOT testing, bring your employer’s CCF (Custody & Control Form) if provided.',
      'Avoid using the restroom for 30 minutes before your appointment.',
      'Empty pockets of unnecessary items upon arrival to maintain chain-of-custody.',
    ],
    faqs: [
      { q: 'What is a Rapid Screen?', a: 'A Rapid Screen (also called Point-of-Care Testing, or POCT) delivers initial results in about 5 minutes using a POCT cup or dip device. It is offered to private employers only. Any non-negative result must still be sent to a certified lab for confirmation before it is considered final.' },
      { q: 'How quickly are results reported?', a: 'Rapid Screens give an initial reading in about 5 minutes. Standard negative lab screens are typically reported within 24 hours, and confirmations for non-negatives take 2–3 business days through our MRO process.' },
      { q: 'Are your collectors DOT-certified?', a: 'Yes. All DOT collections are performed by certified collectors following 49 CFR Part 40 protocols.' },
      { q: 'Can you handle random program administration?', a: 'Yes — we offer full random pool management for employers, including selection, notification, and reporting.' },
    ],
  },
};

export const SERVICE_SLUGS = {
  'Mobile Phlebotomy': 'mobile-phlebotomy',
  'BLS CPR Classes': 'bls-cpr-classes',
  'DNA Testing': 'dna-testing',
  'Drug & Urine Screenings': 'drug-screening',
};

// Services that are announced but not yet available for booking.
export const COMING_SOON_SLUGS = new Set(['dna-testing', 'drug-screening']);
